/**
 * Availability data models with Zod schemas
 * Explicit schema definitions with parsing validation
 */
import { parseISO } from "date-fns";
import { z } from "zod";

// ============================================================================
// SHARED SCHEMAS
// ============================================================================

const TimeSlotSchema = z.object({
  start: z.string(), // Format: "HH:MM" (e.g., "09:00")
  end: z.string(), // Format: "HH:MM" (e.g., "17:00")
});

const ScheduleSchema = z.object({
  monday: z.array(TimeSlotSchema),
  tuesday: z.array(TimeSlotSchema),
  wednesday: z.array(TimeSlotSchema),
  thursday: z.array(TimeSlotSchema),
  friday: z.array(TimeSlotSchema),
  saturday: z.array(TimeSlotSchema),
  sunday: z.array(TimeSlotSchema),
});

export type TimeSlot = z.infer<typeof TimeSlotSchema>;
export type Schedule = z.infer<typeof ScheduleSchema>;

// ============================================================================
// AVAILABILITY SCHEMAS
// ============================================================================

// Domain schema (camelCase)
const AvailabilitySchema = z.object({
  id: z.string(),
  userId: z.string(),
  businessId: z.string(),
  locationId: z.string(),
  schedule: ScheduleSchema,
  timezone: z.string(),
  isActive: z.boolean(),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable(),
});

const CreateAvailabilityRequestSchema = AvailabilitySchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
}).partial({
  timezone: true,
  isActive: true,
});

const UpdateAvailabilityRequestSchema = AvailabilitySchema.omit({
  id: true,
  userId: true,
  businessId: true,
  locationId: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
}).partial();

// DTO schema (snake_case)
const AvailabilityDtoSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  business_id: z.string(),
  location_id: z.string(),
  schedule: ScheduleSchema,
  timezone: z.string(),
  is_active: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable(),
});

export const CreateAvailabilityDtoSchema = AvailabilityDtoSchema.omit({
  id: true,
  created_at: true,
  updated_at: true,
  deleted_at: true,
}).partial({
  timezone: true,
  is_active: true,
});

export const UpdateAvailabilityDtoSchema = AvailabilityDtoSchema.omit({
  id: true,
  user_id: true,
  business_id: true,
  location_id: true,
  created_at: true,
  updated_at: true,
  deleted_at: true,
}).partial();

// Infer DTO types
export type AvailabilityDto = z.infer<typeof AvailabilityDtoSchema>;
export type CreateAvailabilityDto = z.infer<typeof CreateAvailabilityDtoSchema>;
export type UpdateAvailabilityDto = z.infer<typeof UpdateAvailabilityDtoSchema>;

// Infer domain types
export type Availability = z.infer<typeof AvailabilitySchema>;
export type CreateAvailabilityRequest = z.infer<
  typeof CreateAvailabilityRequestSchema
>;
export type UpdateAvailabilityRequest = z.infer<
  typeof UpdateAvailabilityRequestSchema
>;

// ============================================================================
// AVAILABILITY OVERRIDE SCHEMAS
// ============================================================================

const OverrideTypeSchema = z.enum(["unavailable", "custom_hours"]);

// Domain schema (camelCase)
const AvailabilityOverrideSchema = z.object({
  id: z.string(),
  userId: z.string(),
  businessId: z.string(),
  locationId: z.string(),
  effectiveFrom: z.date(),
  effectiveTo: z.date(),
  overrideType: OverrideTypeSchema,
  timeSlots: z.array(TimeSlotSchema),
  reason: z.string().nullable(),
  isActive: z.boolean(),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable(),
});

const CreateAvailabilityOverrideRequestSchema = AvailabilityOverrideSchema.omit(
  {
    id: true,
    createdAt: true,
    updatedAt: true,
    deletedAt: true,
  }
).partial({
  timeSlots: true,
  reason: true,
  isActive: true,
});

// DTO schema (snake_case)
const AvailabilityOverrideDtoSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  business_id: z.string(),
  location_id: z.string(),
  effective_from: z.string(),
  effective_to: z.string(),
  override_type: OverrideTypeSchema,
  time_slots: z.array(TimeSlotSchema),
  reason: z.string().nullable(),
  is_active: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable(),
});

export const CreateAvailabilityOverrideDtoSchema =
  AvailabilityOverrideDtoSchema.omit({
    id: true,
    created_at: true,
    updated_at: true,
    deleted_at: true,
  }).partial({
    time_slots: true,
    reason: true,
    is_active: true,
  });

// Infer DTO types
export type AvailabilityOverrideDto = z.infer<
  typeof AvailabilityOverrideDtoSchema
>;
export type CreateAvailabilityOverrideDto = z.infer<
  typeof CreateAvailabilityOverrideDtoSchema
>;

// Infer domain types
export type AvailabilityOverride = z.infer<typeof AvailabilityOverrideSchema>;
export type CreateAvailabilityOverrideRequest = z.infer<
  typeof CreateAvailabilityOverrideRequestSchema
>;
export type OverrideType = z.infer<typeof OverrideTypeSchema>;

// ============================================================================
// AVAILABILITY CONVERSION UTILITIES
// ============================================================================

export function fromAvailabilityDtoToAvailability(
  dto: AvailabilityDto
): Availability {
  const availabilityDto = AvailabilityDtoSchema.parse(dto);

  return {
    id: availabilityDto.id,
    userId: availabilityDto.user_id,
    businessId: availabilityDto.business_id,
    locationId: availabilityDto.location_id,
    schedule: availabilityDto.schedule,
    timezone: availabilityDto.timezone,
    isActive: availabilityDto.is_active,
    createdAt: parseISO(availabilityDto.created_at),
    updatedAt: parseISO(availabilityDto.updated_at),
    deletedAt: availabilityDto.deleted_at
      ? parseISO(availabilityDto.deleted_at)
      : null,
  };
}

export function fromCreateAvailabilityRequestToCreateAvailabilityDto(
  request: CreateAvailabilityRequest
): CreateAvailabilityDto {
  const createAvailabilityRequest =
    CreateAvailabilityRequestSchema.parse(request);

  return {
    user_id: createAvailabilityRequest.userId,
    business_id: createAvailabilityRequest.businessId,
    location_id: createAvailabilityRequest.locationId,
    schedule: createAvailabilityRequest.schedule,
    timezone: createAvailabilityRequest.timezone,
    is_active: createAvailabilityRequest.isActive,
  };
}

export function fromUpdateAvailabilityRequestToUpdateAvailabilityDto(
  request: UpdateAvailabilityRequest
): UpdateAvailabilityDto {
  const updateAvailabilityRequest =
    UpdateAvailabilityRequestSchema.parse(request);

  return {
    schedule: updateAvailabilityRequest.schedule,
    timezone: updateAvailabilityRequest.timezone,
    is_active: updateAvailabilityRequest.isActive,
  };
}

// ============================================================================
// AVAILABILITY OVERRIDE CONVERSION UTILITIES
// ============================================================================

export function fromAvailabilityOverrideDtoToAvailabilityOverride(
  dto: AvailabilityOverrideDto
): AvailabilityOverride {
  const availabilityOverrideDto = AvailabilityOverrideDtoSchema.parse(dto);

  return {
    id: availabilityOverrideDto.id,
    userId: availabilityOverrideDto.user_id,
    businessId: availabilityOverrideDto.business_id,
    locationId: availabilityOverrideDto.location_id,
    effectiveFrom: parseISO(availabilityOverrideDto.effective_from),
    effectiveTo: parseISO(availabilityOverrideDto.effective_to),
    overrideType: availabilityOverrideDto.override_type,
    timeSlots: availabilityOverrideDto.time_slots,
    reason: availabilityOverrideDto.reason,
    isActive: availabilityOverrideDto.is_active,
    createdAt: parseISO(availabilityOverrideDto.created_at),
    updatedAt: parseISO(availabilityOverrideDto.updated_at),
    deletedAt: availabilityOverrideDto.deleted_at
      ? parseISO(availabilityOverrideDto.deleted_at)
      : null,
  };
}

export function fromCreateAvailabilityOverrideRequestToCreateAvailabilityOverrideDto(
  request: CreateAvailabilityOverrideRequest
): CreateAvailabilityOverrideDto {
  const createAvailabilityOverrideRequest =
    CreateAvailabilityOverrideRequestSchema.parse(request);

  return {
    user_id: createAvailabilityOverrideRequest.userId,
    business_id: createAvailabilityOverrideRequest.businessId,
    location_id: createAvailabilityOverrideRequest.locationId,
    effective_from: createAvailabilityOverrideRequest.effectiveFrom
      .toISOString()
      .split("T")[0],
    effective_to: createAvailabilityOverrideRequest.effectiveTo
      .toISOString()
      .split("T")[0],
    override_type: createAvailabilityOverrideRequest.overrideType,
    time_slots: createAvailabilityOverrideRequest.timeSlots,
    reason: createAvailabilityOverrideRequest.reason,
    is_active: createAvailabilityOverrideRequest.isActive,
  };
}

export function fromTimeSlotDtoToTimeSlot(dto: TimeSlot): TimeSlot {
  return TimeSlotSchema.parse(dto);
}
